import { useFormik } from 'formik';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Checkbox from '../../../shared/components/Checkbox/Checkbox';
import useBreakpoint from '../../../shared/hooks/useBreakpoint';
import FormControl from '../../../shared/components/Form/FormControl';
import PreRegisteredUserFields from './PreRegisteredUserFields';
import RegistrationFormFooter from './RegistrationFormFooter';
import { funcWithArity } from '../../../shared/components/Utils/CustomProptypes'
import { get, isEmpty } from 'lodash';

const RegistrationForm = ({
  onSubmitForm,
  user = {},
  isPreRegisteredUser = false
}) => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const showPreRegisteredFields = isPreRegisteredUser && !isEmpty(user);
  const [state, setState] = useState({
    showPassword: false,
    showConfirmationPassword: false,
    loading: false
  });

  const validationSchema = useMemo(() => Yup.object().shape({
    first_name: Yup.string()
      .required(`${t('influencers.pages.profile.general.first_name')} ${t('influencers.common.is_required')}`),
    last_name: Yup.string()
      .required(`${t('influencers.pages.profile.general.last_name')} ${t('influencers.common.is_required')}`),
    email: Yup.string()
      .email(t('influencers.common.forms.is_not_valid_email'))
      .required(`${t('influencers.common.email')} ${t('influencers.common.is_required')}`),
    password: Yup.string()
      .required(`${t('influencers.common.password')} ${t('influencers.common.is_required')}`)
      .min(8, t('influencers.pages.sign_up.eight_chars_min')),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref('password'), null], t('influencers.pages.sign_up.passwords_must_match')),
    tos_last_accepted: Yup.boolean()
      .oneOf([true], t('influencers.common.forms.is_required_to_be_checked')),
    privacy_policy_last_accepted: Yup.boolean()
      .oneOf([true], t('influencers.common.forms.is_required_to_be_checked')),
    newsletter_consent: Yup.boolean(),
  }), []);

  const formInicialValues = () => {
    const initialValues = {
      first_name: user ? (get(user, 'first_name') || '') : '',
      last_name: user ? (get(user, 'last_name') || '') : '',
      password: user ? (get(user, 'password') || '') : '',
      email: user ? (get(user, 'email') || '') : '',
      password_confirmation: user ? (get(user, 'password_confirmation') || '') : '',
      tos_last_accepted: user ? (get(user, 'tos_last_accepted') || false) : false,
      privacy_policy_last_accepted: user ? (get(user, 'privacy_policy_last_accepted') || false) : false,
      newsletter_consent: user ? (get(user, 'newsletter_consent') || false) : false
    };

    if (isPreRegisteredUser) {
      initialValues['dob'] =  get(user, 'dob') || ''
      initialValues['gender'] =  get(user, 'gender') || ''
      initialValues['ig_handle'] =  get(user, 'ig_handle') || ''
      initialValues['tt_handle'] =  get(user, 'tt_handle') || ''
      initialValues['yt_handle'] =  get(user, 'yt_handle') || ''
      initialValues['link_handle'] =  get(user, 'link_handle') || ''
      initialValues['address_1'] =  get(user, 'address_1') || ''
      initialValues['address_2'] =  get(user, 'address_2') || ''
      initialValues['city'] =  get(user, 'city') || ''
      initialValues['state'] =  get(user, 'state') || ''
      initialValues['zip'] =  get(user, 'zip') || ''
      initialValues['country'] =  get(user, 'country') || ''
    }

    return initialValues;
  };

  const formik = useFormik({
    initialValues: formInicialValues(),
    validationSchema,
    onSubmit: values => {
      onSubmitForm(formik, values, state, setState);
    },
  });

  return(
    <Form onSubmit={formik.handleSubmit}>
      <Row>
        <Col xs={12} sm={6}>
          <Form.Group className='mb-3' >
            <Form.Label>
              {t('influencers.pages.profile.general.first_name')}
            </Form.Label>
            <FormControl
              type='text'
              placeholder={t('influencers.pages.profile.general.first_name')}
              name="first_name"
              data-cy="first_name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.first_name}
              disabled={state.loading}
              error={Boolean(formik.touched.first_name && formik.errors.first_name)} />
            <Form.Text className="text-muted danger-text">
              {formik.touched.first_name && formik.errors.first_name}
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12} sm={6}>
          <Form.Group className='mb-3' >
            <Form.Label>
              {t('influencers.pages.profile.general.last_name')}
            </Form.Label>
            <FormControl
              type='text'
              placeholder={t('influencers.pages.profile.general.last_name')}
              name="last_name"
              data-cy="last_name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.last_name}
              disabled={state.loading}
              error={Boolean(formik.touched.last_name && formik.errors.last_name)} />
            <Form.Text className="text-muted danger-text">
              {formik.touched.last_name && formik.errors.last_name}
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group className='mb-3' >
            <Form.Label>
              {t('influencers.common.email_address')}
            </Form.Label>
            <FormControl
              type='text'
              placeholder={t('influencers.common.email_address')}
              name="email"
              data-cy="email_address"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              disabled={state.loading}
              readOnly={isPreRegisteredUser}
              error={Boolean(formik.touched.email && formik.errors.email)} />
            <Form.Text className="text-muted danger-text">
              {formik.touched.email && formik.errors.email}
            </Form.Text>
          </Form.Group>
        </Col>

        {
          showPreRegisteredFields &&
          <PreRegisteredUserFields formik={formik} disabled={state.loading} />
        }

        <Col xs={12} sm={6}>
          <Form.Group className='mb-3' >
            <Form.Label>
              {t('influencers.common.password')}
            </Form.Label>
            <FormControl
              type={state.showPassword ? 'text' : 'password'}
              placeholder={t('influencers.common.password')}
              name="password"
              data-cy="password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              disabled={state.loading}
              append={
                <button className='btn'
                  type='button'
                  tabIndex={-1}
                  onClick={() =>
                    setState({ ...state, showPassword: !state.showPassword })
                  }>
                  {
                    state.showPassword
                      ? <i className="fal fa-eye-slash"></i>
                      : <i className="fal fa-eye"></i>
                  }
                </button>
              }
              error={Boolean(formik.touched.password && formik.errors.password)} />
            <Form.Text className="text-muted danger-text">
              {formik.touched.password && formik.errors.password}
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12} sm={6}>
          <Form.Group className='mb-3' >
            <Form.Label>
              {t('influencers.common.password_confirmation')}
            </Form.Label>
            <FormControl
              type={state.showConfirmationPassword ? 'text' : 'password'}
              placeholder={t('influencers.common.password')}
              name="password_confirmation"
              data-cy="password_confirmation"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password_confirmation}
              disabled={state.loading}
              append={
                <button className='btn'
                  tabIndex={-1}
                  type='button'
                  onClick={() =>
                    setState({ ...state, showConfirmationPassword: !state.showConfirmationPassword })
                  }>
                  {
                    state.showConfirmationPassword
                      ? <i className="fal fa-eye-slash"></i>
                      : <i className="fal fa-eye"></i>
                  }
                </button>
              }
              error={Boolean(formik.touched.password_confirmation && formik.errors.password_confirmation)} />
            <Form.Text className="text-muted danger-text">
              {formik.touched.password_confirmation && formik.errors.password_confirmation}
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group className='mb-3' >
            <Checkbox
              name="tos_last_accepted"
              onChange={formik.handleChange}
              value={formik.values.tos_last_accepted}
              disabled={state.loading}
              label={
                <span className="text-1">
                  {t('influencers.pages.sign_up.i_agree_to')}{' '}
                  <a href='/influencers/terms-of-service' target="_blank">
                    {t('influencers.pages.sign_up.terms_and_conditions')}
                  </a>
                </span>
              }
            />
            <Form.Text className="text-muted danger-text">
              {formik.touched.tos_last_accepted && formik.errors.tos_last_accepted}
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group className='mb-3' >
            <Checkbox
              name="privacy_policy_last_accepted"
              onChange={formik.handleChange}
              value={formik.values.privacy_policy_last_accepted}
              disabled={state.loading}
              label={
                <span className="text-1">
                  {t('influencers.pages.sign_up.i_agree_to_2')}{' '}
                  <a href='/influencers/privacy-policy' target="_blank">
                    {t('influencers.pages.sign_up.privacy_policy')}
                  </a>
                </span>
              }
            />
            <Form.Text className="text-muted danger-text">
              {formik.touched.privacy_policy_last_accepted && formik.errors.privacy_policy_last_accepted}
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group className='mb-3' >
            <Checkbox
              name="newsletter_consent"
              onChange={formik.handleChange}
              value={formik.values.newsletter_consent}
              disabled={state.loading}
              label={
                <span className="text-1">
                  {t('influencers.pages.sign_up.subscribe_to_newsletter')}
                </span>
              }
            />
          </Form.Group>
        </Col>
      </Row>
      <hr className={classNames('my-6', {
        noMargin: breakpoint === 'xs'
      })} />
      <RegistrationFormFooter
        formik={formik}
        loading={state.loading}
        showPreRegisteredFields={showPreRegisteredFields}
      />
    </Form>
  );
};

RegistrationForm.propTypes = {
  onSubmitForm: funcWithArity(4).isRequired,
  user: PropTypes.object,
  isPreRegisteredUser: PropTypes.bool
};

export default RegistrationForm;
